// GoalsView.jsx — the Goal tab's middle section: the dashboard of every goal
// this user may see (the default), or one goal in detail. The pencil beside
// "New Goal" swaps the middle back to the manuscript itself.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LinkIcon from '@mui/icons-material/Link';
import SendIcon from '@mui/icons-material/Send';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import {
  METRICS, QUOTES, SORTS, goalStatus, progressOf, paceOf, paceText, smartRows,
  daysBetween, today, fmtDay, fmtStamp, fmtRate, byDue, stampDay,
} from './goalModel';
import { GoalIcon, GoalGlyph, StatusPill, Priority, Avatar, Bar, iconFor } from './goalBits';

const FILTERS = [['all', 'All'], ['active', 'Active'], ['risk', 'At risk'], ['done', 'Completed'], ['overdue', 'Overdue']];
const ACTIVE = ['progress', 'notstarted', 'risk'];

export const scopeLabelFor = (goal, chapters) => {
  if (goal.metric !== 'words' || !goal.scope || goal.scope === 'book') return 'Whole manuscript';
  const i = chapters.findIndex((c) => c.id === goal.scope);
  return i === -1 ? 'A deleted chapter' : `Chapter ${i + 1} · ${chapters[i].title}`;
};

function dueLine(goal, status) {
  if (status === 'done') return <>Completed {fmtDay(stampDay(goal.completed_at))}</>;
  if (status === 'cancelled') return <>Cancelled</>;
  if (!goal.due_date) return <>No due date</>;
  const d = daysBetween(today(), goal.due_date);
  return (
    <>
      Due {fmtDay(goal.due_date)}
      {d < 0 && <b className="gv-late"> · {-d} day{d === -1 ? '' : 's'} overdue</b>}
      {d === 0 && <b className="gv-soon"> · today</b>}
      {d > 0 && d <= 7 && <b className="gv-soon"> · {d} day{d === 1 ? '' : 's'} left</b>}
    </>
  );
}

/** A small popover menu that closes on any click outside it. */
function Menu({ items, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [onClose]);
  return (
    <div className="gv-menu" ref={ref} role="menu">
      {items.filter(Boolean).map((it) => (it === '-' ? <div key={Math.random()} className="gv-menu-div" /> : (
        <button key={it.label} type="button" role="menuitem" className={`gv-mi${it.bad ? ' is-bad' : ''}`}
          onClick={() => { onClose(); it.run(); }}>
          {it.icon}{it.label}{it.checked && <span className="gv-mi-check">✓</span>}
        </button>
      )))}
    </div>
  );
}

/** The goal's actions, minus the ones this viewer may not take. */
export function useGoalActions(api, { onOpen, onEdit }) {
  return (goal, live) => {
    const s = goalStatus(goal, live);
    const closed = s === 'done' || s === 'cancelled';
    return [
      { label: 'Open goal', run: () => onOpen(goal._id) },
      goal.can_edit && { label: 'Edit goal', run: () => onEdit(goal) },
      goal.can_edit && { label: 'Duplicate', run: () => api.create(duplicateBody(goal)) },
      closed && (s === 'cancelled' ? goal.can_edit : goal.can_complete)
        && { label: s === 'cancelled' ? 'Restore' : 'Reopen', run: () => reopen(api, goal) },
      !closed && goal.can_complete && { label: 'Mark complete', run: () => api.setComplete(goal._id, true) },
      !closed && goal.can_edit && { label: 'Cancel goal', run: () => confirmCancel(api, goal) },
      goal.can_edit && '-',
      goal.can_edit && { label: 'Delete', bad: true, run: () => confirmDelete(api, goal) },
    ];
  };
}

function duplicateBody(goal) {
  return {
    name: `${goal.name} (copy)`, summary: goal.summary || '', detail: goal.detail || '', why: goal.why || '',
    icon: goal.icon, priority: goal.priority, due_date: goal.due_date, metric: goal.metric || null,
    ...(goal.metric && goal.metric !== 'tasks' ? { target: goal.target } : {}),
    ...(goal.metric === 'words' ? { scope: goal.scope || 'book' } : {}),
    checklist: (goal.checklist || []).map((i) => i.text),
    assignees: goal.assignees || [],
  };
}

function reopen(api, goal) {
  // A cancelled goal comes back through the edit endpoint; a completed one
  // through "not complete".
  return goal.status === 'cancelled' ? api.update(goal._id, { status: 'in_progress' }) : api.setComplete(goal._id, false);
}
function confirmCancel(api, goal) {
  if (window.confirm(`Cancel “${goal.name}”? You can restore it later.`)) api.cancel(goal._id);
}
function confirmDelete(api, goal) {
  if (window.confirm(`Delete “${goal.name}” permanently?`)) return api.remove(goal._id);
  return null;
}

// ── Dashboard ─────────────────────────────────────────────────────────────

function Stat({ icon, value, label }) {
  return (
    <div className="gv-stat">
      {icon}
      <div><b>{value}</b><small>{label}</small></div>
    </div>
  );
}

function GoalCard({ goal, live, chapters, actionsFor, onOpen }) {
  const [menu, setMenu] = useState(false);
  const s = goalStatus(goal, live);
  const prog = progressOf(goal, live);
  const pt = paceText(goal, live);
  const assignee = (goal.assignees || [])[0];
  return (
    <article className="gv-card" tabIndex={0} onClick={() => onOpen(goal._id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(goal._id); }}>
      <GoalIcon icon={iconFor(goal)} />
      <div className="gv-card-body">
        <h3>{goal.name}</h3>
        <StatusPill status={s} />
        <div className="gv-pbar">
          <Bar pct={prog ? prog.pct : s === 'done' ? 100 : 0} status={s} />
          <b>{prog ? prog.pct : s === 'done' ? 100 : 0}%</b>
        </div>
        <div className="gv-meta">
          {prog
            ? <>{prog.cur.toLocaleString()} of {prog.target.toLocaleString()} {prog.unit}
              {goal.metric === 'words' && goal.scope && goal.scope !== 'book'
                && <span className="gv-faint"> · {scopeLabelFor(goal, chapters).split(' · ')[0]}</span>}</>
            : goal.summary || 'No measure'}
          {pt && <><br /><span className="gv-faint">{pt}</span></>}
        </div>
        <div className="gv-foot">
          <div className="gv-meta">{dueLine(goal, s)}</div>
          <div className="gv-foot-r">
            {assignee && <Avatar id={assignee} name={(goal.assignee_usernames || [])[0]} />}
            <button type="button" className="gv-view" onClick={(e) => { e.stopPropagation(); onOpen(goal._id); }}>View</button>
          </div>
        </div>
      </div>
      <button type="button" className="gv-kebab" aria-label="Goal actions"
        onClick={(e) => { e.stopPropagation(); setMenu((v) => !v); }}>
        <MoreVertIcon fontSize="small" />
      </button>
      {menu && (
        <div onClick={(e) => e.stopPropagation()} role="presentation">
          <Menu items={actionsFor(goal, live)} onClose={() => setMenu(false)} />
        </div>
      )}
    </article>
  );
}

function Dashboard({ api, live, chapters, actionsFor, onOpen, onNew, onWrite }) {
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('due');
  const [query, setQuery] = useState('');
  const { goals, writing } = api;

  const counts = useMemo(() => {
    const c = { all: goals.length, active: 0, risk: 0, done: 0, overdue: 0 };
    goals.forEach((g) => {
      const s = goalStatus(g, live);
      if (ACTIVE.includes(s)) c.active += 1;
      if (s === 'risk') c.risk += 1;
      if (s === 'done') c.done += 1;
      if (s === 'overdue') c.overdue += 1;
    });
    return c;
  }, [goals, live]);

  const q = query.trim().toLowerCase();
  const list = goals
    .filter((g) => {
      const s = goalStatus(g, live);
      if (filter === 'active' && !ACTIVE.includes(s)) return false;
      if (filter !== 'all' && filter !== 'active' && s !== filter) return false;
      return !q || `${g.name} ${g.summary || ''} ${g.detail || ''}`.toLowerCase().includes(q);
    })
    .sort(sort === 'progress'
      ? (a, b) => (progressOf(b, live)?.pct || 0) - (progressOf(a, live)?.pct || 0)
      : SORTS[sort]);

  const next = goals.filter((g) => g.due_date && ['progress', 'notstarted', 'risk'].includes(goalStatus(g, live))).sort(byDue)[0];
  const streak = writing?.streak || 0;

  return (
    <>
      <div className="gv-top">
        <label className="gv-search">
          <SearchIcon fontSize="small" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search your goals, tasks, or book…" />
        </label>
      </div>
      <div className="gv-head">
        <div><h2>Your Goals</h2><p>Turn your dreams into finished chapters.</p></div>
        <div className="gv-acts">
          <button type="button" className="gv-btn is-pri" onClick={onNew}><AddIcon fontSize="small" />New Goal</button>
          <button type="button" className="gv-iconbtn" title="Back to writing" aria-label="Edit the manuscript" onClick={onWrite}>
            <EditOutlinedIcon fontSize="small" />
          </button>
        </div>
      </div>
      <div className="gv-stats">
        <Stat icon={<GoalIcon icon="book" size={38} />} value={(live.total || 0).toLocaleString()} label="words in manuscript" />
        <Stat icon={<GoalIcon icon="bulb" size={38} />} value={(writing?.last7 || 0).toLocaleString()} label="words in the last 7 days" />
        <Stat icon={<span className="gv-icon gv-icon--pen" style={{ width: 38, height: 38 }}><LocalFireDepartmentIcon fontSize="small" /></span>} value={`${streak} day${streak === 1 ? '' : 's'}`} label="writing streak" />
        <Stat icon={<GoalIcon icon="image" size={38} />}
          value={next ? `${daysBetween(today(), next.due_date)} days` : '—'}
          label={next ? `until “${next.name}”` : 'no upcoming deadlines'} />
      </div>
      <div className="gv-filters">
        {FILTERS.map(([key, label]) => (
          <button key={key} type="button" className={`gv-chip${filter === key ? ' is-on' : ''}`} onClick={() => setFilter(key)}>
            {label} ({counts[key]})
          </button>
        ))}
        <select className="gv-sort" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort goals">
          <option value="due">Sort: Due date</option>
          <option value="priority">Sort: Priority</option>
          <option value="progress">Sort: Progress</option>
          <option value="az">Sort: A–Z</option>
        </select>
      </div>
      <div className="gv-grid">
        {list.length ? list.map((g) => (
          <GoalCard key={g._id} goal={g} live={live} chapters={chapters} actionsFor={actionsFor} onOpen={onOpen} />
        )) : (
          <div className="gv-empty"><b>No goals match.</b>Try another filter, or create a new goal.</div>
        )}
      </div>
    </>
  );
}

// ── Detail ────────────────────────────────────────────────────────────────

function Ring({ pct, status }) {
  const r = 56;
  const c = 2 * Math.PI * r;
  return (
    <div className="gv-ring">
      <svg width="132" height="132" aria-hidden="true">
        <circle cx="66" cy="66" r={r} className="gv-ring-track" />
        <circle cx="66" cy="66" r={r} className={`gv-ring-arc gv-ring-arc--${status}`}
          strokeDasharray={c.toFixed(1)} strokeDashoffset={(c * (1 - pct / 100)).toFixed(1)} />
      </svg>
      <div className="gv-ring-v"><div><b>{pct}%</b><small>complete</small></div></div>
    </div>
  );
}

function Chart({ goal, pace, status, unit }) {
  const h = (goal.history || []).slice(-14);
  if (!h.length) return <p className="gv-faint">The daily chart fills in as progress is made.</p>;
  const W = 420, H = 120, bw = W / 14;
  const need = pace && status !== 'done' ? pace.perDay : 0;
  const max = Math.max(1, ...h.map((x) => x.value), need) * 1.15;
  const ny = H - (need / max) * H;
  const lastDay = h[h.length - 1].day;
  return (
    <>
      <svg className="gv-chart" viewBox={`0 0 ${W} ${H + 18}`} role="img"
        aria-label="Daily progress, last 14 days">
        {h.map((x, i) => {
          const bh = (x.value / max) * H;
          const tone = x.day === lastDay ? 'today' : need && x.value >= need ? 'met' : 'day';
          return (
            <rect key={x.day} x={(i * bw + 3).toFixed(1)} y={(H - bh).toFixed(1)} width={(bw - 6).toFixed(1)}
              height={Math.max(bh, x.value ? 2 : 0).toFixed(1)} rx="3" className={`gv-chart-bar is-${tone}`}>
              <title>{`${fmtDay(x.day)}: ${x.value} ${unit}`}</title>
            </rect>
          );
        })}
        {need > 0 && <line x1="0" x2={W} y1={ny.toFixed(1)} y2={ny.toFixed(1)} className="gv-chart-need" />}
        <line x1="0" x2={W} y1={H} y2={H} className="gv-chart-axis" />
        {/* Every third day, and Today — but not a label crowding Today's. */}
        {h.map((x, i) => (((i % 3 === 0 && i < h.length - 2) || i === h.length - 1) ? (
          <text key={`t${x.day}`} x={(i * bw + bw / 2).toFixed(1)} y={H + 14} textAnchor="middle" className="gv-chart-label">
            {i === h.length - 1 ? 'Today' : fmtDay(x.day).replace(/, \d{4}$/, '')}
          </text>
        ) : null))}
      </svg>
      <div className="gv-legend">
        <span><i className="is-day" />Daily {unit}</span>
        {need > 0 && <span><i className="is-need" />Needed per day ({fmtRate(need)})</span>}
      </div>
    </>
  );
}

function Detail({ api, goal, live, chapters, currentUserId, actionsFor, onBack, onEdit, onContinue, tab, setTab }) {
  const [menu, setMenu] = useState(null); // 'more' | 'prio' | 'assign'
  const [text, setText] = useState({ item: '', note: '', title: '', url: '', comment: '' });
  const set = (k) => (e) => setText((t) => ({ ...t, [k]: e.target.value }));
  const s = goalStatus(goal, live);
  const prog = progressOf(goal, live);
  const pace = paceOf(goal, prog);
  const metric = METRICS[goal.metric];
  const closed = s === 'done' || s === 'cancelled';
  const manual = goal.metric && !metric?.auto;
  const assignee = (goal.assignees || [])[0];
  const assigneeName = (goal.assignee_usernames || [])[0];
  const pct = prog ? prog.pct : s === 'done' ? 100 : 0;
  const scope = scopeLabelFor(goal, chapters);
  const team = api.isAuthor ? api.team : api.team.filter((u) => String(u.id) === String(currentUserId));
  const cont = {
    words: 'Continue Writing', chapters: 'Continue Writing', pages: 'Continue Revising',
    images: 'Add Images', tasks: 'Open Checklist', sessions: 'Start a Session',
  }[goal.metric] || 'Continue Writing';

  const paceBox = (() => {
    if (s === 'done') return <div className="gv-pacebox is-ok">🎉 Goal reached on {fmtDay(stampDay(goal.completed_at))}. Nicely done.</div>;
    if (s === 'cancelled' || !pace) return null;
    if (s === 'overdue') {
      return (
        <div className="gv-pacebox is-bad">
          <b>{-pace.left} day{pace.left === -1 ? '' : 's'} overdue.</b> {Math.round(pace.remain).toLocaleString()} {prog.unit} to go.
          {' '}Extend the due date or mark it complete.
        </div>
      );
    }
    return (
      <div className={`gv-pacebox${s === 'risk' ? ' is-warn' : ''}`}>
        <b>{s === 'risk' ? 'Falling behind:' : pace.ahead ? 'Ahead of schedule:' : 'On track:'}</b>
        {' '}aim for {fmtRate(pace.perDay)} {prog.unit}/day for {pace.left} more day{pace.left === 1 ? '' : 's'}.
        {' '}Recent average is {fmtRate(pace.avg)}/day.
      </div>
    );
  })();

  const checklist = goal.checklist || [];
  const tabs = [
    ['progress', 'Progress'], ['tasks', 'Tasks', checklist.length], ['notes', 'Notes', (goal.notes || []).length],
    ['resources', 'Resources', (goal.links || []).length], ['comments', 'Comments', (goal.comments || []).length],
  ];

  const submit = async (key, run) => {
    if (!text[key].trim()) return;
    if (await run()) setText((t) => ({ ...t, [key]: '', ...(key === 'url' ? { title: '' } : {}) }));
  };

  return (
    <>
      <button type="button" className="gv-back" onClick={onBack}><ArrowBackIcon fontSize="small" />Back to Goals</button>
      <div className="gv-dhead">
        <GoalIcon icon={iconFor(goal)} size={72} square />
        <div><h2>{goal.name}</h2><p>{goal.summary || ''}</p></div>
        <div className="gv-dhead-r">
          <StatusPill status={s} large />
          <button type="button" className="gv-iconbtn" aria-label="More actions" onClick={() => setMenu(menu === 'more' ? null : 'more')}>
            <MoreVertIcon fontSize="small" />
          </button>
          {menu === 'more' && <Menu items={actionsFor(goal, live).slice(1)} onClose={() => setMenu(null)} />}
        </div>
      </div>

      <div className="gv-dtop">
        <div className="gv-cardbox">
          <div className="gv-ringwrap">
            <Ring pct={pct} status={s} />
            <div className="gv-facts">
              <div>
                <GoalGlyph icon={metric?.icon || iconFor(goal)} />
                {prog
                  ? <b>{prog.cur.toLocaleString()} of {prog.target.toLocaleString()} {prog.unit}</b>
                  : <b>No measure</b>}
                {goal.metric === 'words' && <small>· live from manuscript</small>}
              </div>
              <div><CalendarTodayOutlinedIcon fontSize="small" /><span>{dueLine(goal, s)}</span></div>
              <div><ScheduleOutlinedIcon fontSize="small" /><span className="gv-faint">Created {fmtDay(stampDay(goal.created_at))}</span></div>
            </div>
          </div>
          <div className="gv-strip">
            <div>
              <label>Priority</label>
              <div className="gv-val gv-anchor">
                {goal.can_edit ? (
                  <button type="button" className="gv-selbtn" onClick={() => setMenu(menu === 'prio' ? null : 'prio')}>
                    <Priority value={goal.priority} /> ▾
                  </button>
                ) : <Priority value={goal.priority} />}
                {menu === 'prio' && (
                  <Menu onClose={() => setMenu(null)} items={['HIGH', 'MEDIUM', 'LOW'].map((p) => ({
                    label: p, checked: (goal.priority || 'MEDIUM') === p, run: () => api.update(goal._id, { priority: p }),
                  }))} />
                )}
              </div>
            </div>
            <div><label>Scope</label><div className="gv-val"><span className="gv-ellipsis" title={scope}>{scope}</span></div></div>
            <div>
              <label>Requestor</label>
              <div className="gv-val"><span className="gv-person"><Avatar id={goal.created_by} name={goal.requestor_username} />{goal.requestor_username || '—'}</span></div>
            </div>
            <div>
              <label>Assigned to</label>
              <div className="gv-val gv-anchor">
                {goal.can_edit ? (
                  <button type="button" className="gv-selbtn" onClick={() => setMenu(menu === 'assign' ? null : 'assign')}>
                    {assignee ? <span className="gv-person"><Avatar id={assignee} name={assigneeName} size={22} />{assigneeName}</span> : 'Unassigned'} ▾
                  </button>
                ) : (assignee ? <span className="gv-person"><Avatar id={assignee} name={assigneeName} size={22} />{assigneeName}</span> : 'Unassigned')}
                {menu === 'assign' && (
                  <Menu onClose={() => setMenu(null)} items={[
                    ...team.map((u) => ({
                      label: u.username, icon: <Avatar id={u.id} name={u.username} />,
                      checked: String(assignee) === String(u.id), run: () => api.update(goal._id, { assignees: [u.id] }),
                    })),
                    '-',
                    { label: 'Unassigned', run: () => api.update(goal._id, { assignees: [] }) },
                  ]} />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="gv-cardbox gv-actcol">
          <button type="button" className="gv-btn is-pri" disabled={closed} onClick={() => onContinue(goal)}>
            <GoalGlyph icon={metric?.icon === 'type' ? 'type' : 'pen'} />{cont}
          </button>
          {goal.can_edit && (
            <div className="gv-row">
              <button type="button" className="gv-btn" onClick={() => onEdit(goal)}><EditOutlinedIcon fontSize="small" />Edit Goal</button>
              {assignee ? (
                <button type="button" className="gv-btn" onClick={() => api.update(goal._id, { assignees: [] })}>
                  <PersonRemoveOutlinedIcon fontSize="small" />Unassign
                </button>
              ) : (
                <span className="gv-anchor">
                  <button type="button" className="gv-btn" onClick={() => setMenu(menu === 'assign2' ? null : 'assign2')}>
                    <PersonAddAltOutlinedIcon fontSize="small" />Assign
                  </button>
                  {menu === 'assign2' && (
                    <Menu onClose={() => setMenu(null)} items={team.map((u) => ({
                      label: u.username, icon: <Avatar id={u.id} name={u.username} />,
                      run: () => api.update(goal._id, { assignees: [u.id] }),
                    }))} />
                  )}
                </span>
              )}
            </div>
          )}
          {manual && !closed && goal.can_complete && (
            <div className="gv-row gv-log">
              <button type="button" className="gv-btn is-sm" aria-label="Log one less" onClick={() => api.logProgress(goal._id, -1)}>−</button>
              <span>Log progress ({metric.unit})</span>
              <button type="button" className="gv-btn is-sm" aria-label="Log one more" onClick={() => api.logProgress(goal._id, 1)}>+</button>
            </div>
          )}
          {paceBox}
        </div>
      </div>

      <div className="gv-tabs" role="tablist">
        {tabs.map(([key, label, n]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? 'is-on' : ''} onClick={() => setTab(key)}>
            {label}{n != null && <span className="gv-n">{n}</span>}
          </button>
        ))}
      </div>

      {tab === 'progress' && (
        <>
          <div className="gv-prow">
            <div className="gv-cardbox">
              <h4>{metric ? `${metric.label} progress` : 'Progress'} {prog && <small>{prog.cur.toLocaleString()} / {prog.target.toLocaleString()} {prog.unit}</small>}</h4>
              <div className="gv-bigbar">
                <i style={{ width: `${pct}%` }} />
                {pace && !closed && <span className="gv-exp" style={{ left: `${Math.min(100, pace.elapsedPct)}%` }} title="Where you’d be on an even pace" />}
              </div>
              <div className="gv-bigbar-l">
                <span>{pct}% complete</span>
                <span>{pace && !closed ? `▏ marker = even pace (${Math.round(pace.elapsedPct)}% of time used)` : ''}</span>
              </div>
              {prog ? <Chart goal={goal} pace={pace} status={s} unit={prog.unit} /> : <p className="gv-faint">Give this goal a measure to chart its daily progress.</p>}
            </div>
            <div className="gv-cardbox">
              <h4>Recent Activity</h4>
              <div className="gv-tl">
                {(goal.activity || []).slice(0, 5).map((a, i) => (
                  <div className="gv-tli" key={`${a.at}-${i}`}>
                    <span className="gv-dot">{{ add: '✎', done: '✓', assign: '👤', comment: '💬' }[a.kind] || '•'}</span>
                    {a.who} {a.text}<small>{fmtStamp(a.at)}</small>
                  </div>
                ))}
                {!(goal.activity || []).length && <p className="gv-faint">No activity yet.</p>}
              </div>
            </div>
            <div className="gv-quote"><span>{QUOTES[goal.metric] || QUOTES.none}</span><GoalIcon icon="book" size={30} /></div>
          </div>
          <div className="gv-prow2">
            <div className="gv-cardbox">
              <h4>Goal criteria <small>SMART check</small></h4>
              <SmartList goal={goal} prog={prog} pace={pace} scope={scope} />
            </div>
            <div className="gv-cardbox">
              <h4>Detail</h4>
              <div className={`gv-detailtxt${goal.detail ? '' : ' is-none'}`}>
                {goal.detail || 'No details yet. Use Edit Goal to describe what “done” looks like.'}
              </div>
              {goal.why && <><h4 className="gv-h4-gap">Why it matters</h4><div className="gv-detailtxt">{goal.why}</div></>}
            </div>
          </div>
        </>
      )}

      {tab === 'tasks' && (
        <div className="gv-cardbox gv-narrow">
          <h4>Tasks <small>{checklist.filter((i) => i.done).length} of {checklist.length} done{goal.metric === 'tasks' ? ' · drives this goal’s progress' : ''}</small></h4>
          {checklist.map((i) => (
            <div key={i._id} className={`gv-task${i.done ? ' is-done' : ''}`}>
              <button type="button" className={`gv-cbx${i.done ? ' is-on' : ''}`} aria-label="Toggle task"
                disabled={!goal.can_complete} onClick={() => api.toggleItem(goal._id, i._id, !i.done)}>{i.done ? '✓' : ''}</button>
              <span className="gv-tt">{i.text}</span>
              {goal.can_edit && <button type="button" className="gv-del" aria-label="Delete task" onClick={() => api.removeItem(goal._id, i._id)}>×</button>}
            </div>
          ))}
          {!checklist.length && <p className="gv-faint">Break this goal into small steps.</p>}
          {goal.can_edit && (
            <div className="gv-addrow">
              <input className="gv-inp" value={text.item} onChange={set('item')} placeholder="Add a task and press Enter…"
                onKeyDown={(e) => { if (e.key === 'Enter') submit('item', () => api.addItem(goal._id, text.item)); }} />
              <button type="button" className="gv-btn is-pri" onClick={() => submit('item', () => api.addItem(goal._id, text.item))}>Add</button>
            </div>
          )}
        </div>
      )}

      {tab === 'notes' && (
        <div className="gv-cardbox gv-narrow">
          <h4>Notes</h4>
          {(goal.notes || []).slice().reverse().map((n) => (
            <div key={n._id} className="gv-note">{n.text}<small>{n.who ? `${n.who} · ` : ''}{fmtStamp(n.at)}</small></div>
          ))}
          {!(goal.notes || []).length && <p className="gv-faint">Capture ideas, blockers and decisions here.</p>}
          <textarea className="gv-inp" rows={3} value={text.note} onChange={set('note')} placeholder="Write a note…" />
          <div className="gv-right"><button type="button" className="gv-btn is-pri is-sm" onClick={() => submit('note', () => api.addNote(goal._id, text.note))}>Save note</button></div>
        </div>
      )}

      {tab === 'resources' && (
        <div className="gv-cardbox gv-narrow">
          <h4>Resources</h4>
          {(goal.links || []).map((l) => (
            <div key={l._id} className="gv-res">
              <span className="gv-res-ic"><LinkIcon fontSize="small" /></span>
              <a href={l.url} target="_blank" rel="noopener noreferrer">{l.title}<small>{l.url}</small></a>
              {(goal.can_edit || l.by === String(currentUserId)) && (
                <button type="button" className="gv-del" aria-label="Remove link" onClick={() => api.removeLink(goal._id, l._id)}>×</button>
              )}
            </div>
          ))}
          {!(goal.links || []).length && <p className="gv-faint">Link research, references and reference images.</p>}
          {goal.can_complete && (
            <div className="gv-addrow">
              <input className="gv-inp" value={text.title} onChange={set('title')} placeholder="Title" />
              <input className="gv-inp" value={text.url} onChange={set('url')} placeholder="https://…" />
              <button type="button" className="gv-btn is-pri" onClick={() => submit('url', () => api.addLink(goal._id, text.title, text.url))}>Add</button>
            </div>
          )}
        </div>
      )}

      {tab === 'comments' && (
        <div className="gv-cm-wrap">
          <div className="gv-cardbox">
            <h4>Comment History</h4>
            {(goal.comments || []).map((c) => (
              <div key={c._id} className="gv-cmt">
                <Avatar id={c.user_id} name={c.username} size={32} />
                <div className="gv-bub"><b>{c.username}</b><small>{fmtStamp(c.created_at)}</small><br />{c.text}</div>
              </div>
            ))}
            {!(goal.comments || []).length && <p className="gv-faint gv-center">No comments yet.</p>}
            <div className="gv-cmbox">
              <textarea className="gv-inp" rows={2} value={text.comment} onChange={set('comment')} placeholder="Start typing here…"
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit('comment', () => api.comment(goal._id, text.comment)); }} />
              <button type="button" className="gv-send" aria-label="Send" disabled={!text.comment.trim()}
                onClick={() => submit('comment', () => api.comment(goal._id, text.comment))}><SendIcon fontSize="small" /></button>
            </div>
          </div>
          <div className="gv-cardbox">
            <h4>People</h4>
            <div className="gv-meta2">
              <label>Requestor</label><span className="gv-person"><Avatar id={goal.created_by} name={goal.requestor_username} />{goal.requestor_username || '—'}</span>
              <label>Assigned</label><span className="gv-person">{assignee ? <><Avatar id={assignee} name={assigneeName} />{assigneeName}</> : 'Unassigned'}</span>
            </div>
            <p className="gv-faint gv-small">Comments notify the requestor and the assignee.</p>
          </div>
        </div>
      )}

      <div className="gv-dfoot">
        {closed && (s === 'cancelled' ? goal.can_edit : goal.can_complete) && <button type="button" className="gv-btn" onClick={() => reopen(api, goal)}>{s === 'cancelled' ? 'Restore Goal' : 'Reopen Goal'}</button>}
        {!closed && goal.can_edit && <button type="button" className="gv-btn" onClick={() => confirmCancel(api, goal)}>Cancel Goal</button>}
        {!closed && goal.can_complete && <button type="button" className="gv-btn is-ok" onClick={() => api.setComplete(goal._id, true)}>✓ Mark Complete</button>}
        {goal.can_edit && <button type="button" className="gv-btn is-danger" onClick={async () => { if (await confirmDelete(api, goal)) onBack(); }}>Delete</button>}
        {goal.can_edit && <button type="button" className="gv-btn is-pri" onClick={() => onEdit(goal)}>Update Goal</button>}
        <button type="button" className="gv-btn is-ghost" onClick={onBack}>Close</button>
      </div>
    </>
  );
}

export function SmartList({ goal, prog, pace, scope }) {
  return (
    <div className="gv-smart">
      {smartRows(goal, prog, pace, scope).map(([k, label, st, txt]) => (
        <div className="gv-sm" key={k}>
          <span className={`gv-k is-${st}`}>{st === 'y' ? '✓' : k}</span><b>{label}</b><span>{txt}</span>
        </div>
      ))}
    </div>
  );
}

export { GoalCard };

export default function GoalsView({
  api, live, chapters, currentUserId, view, onOpen, onBack, onNew, onEdit, onWrite, onContinue, onToggleList,
}) {
  const [tab, setTab] = useState('progress');
  useEffect(() => { setTab(view.tab || 'progress'); }, [view.id, view.tab]);
  const actionsFor = useGoalActions(api, { onOpen, onEdit });
  const goal = view.mode === 'detail' ? api.goals.find((g) => g._id === view.id) : null;

  return (
    <div className="gv-root">
      <div className="gv-sheet">
        {/* Shown only on narrow screens, where the Goal list floats over this page. */}
        <button type="button" className="gv-listbtn" onClick={onToggleList}>☰ Goals list</button>
        {api.error && (
          <div className="gv-error" role="alert">
            {api.error}
            <button type="button" onClick={api.clearError} aria-label="Dismiss">×</button>
          </div>
        )}
        {goal ? (
          <Detail api={api} goal={goal} live={live} chapters={chapters} currentUserId={currentUserId}
            actionsFor={actionsFor} onBack={onBack} onEdit={onEdit} onContinue={onContinue} tab={tab} setTab={setTab} />
        ) : (
          <Dashboard api={api} live={live} chapters={chapters} actionsFor={actionsFor}
            onOpen={onOpen} onNew={onNew} onWrite={onWrite} />
        )}
      </div>
    </div>
  );
}

